const CONFIG_SELECTOR = '[data-bosp-candyrack-skin-config]';
const PRODUCT_ID_CLASS_PREFIX = 'candyrack-ppp-product-id-';
const CART_PAGE_MOUNT_SELECTOR = '[data-bosp-cart-cross-sell-mount]';
const productDataCache = new Map();
const productDataByHandleCache = new Map();
const pendingProductIds = new Set();
const pendingProductHandles = new Set();
const pendingCartOfferHandles = new Set();
let cartPageSourceMarkup = '';
let candyRackScanScheduled = false;

function getConfig() {
  const source = document.querySelector(CONFIG_SELECTOR);

  return {
    previous: source?.dataset.previousLabel || '',
    next: source?.dataset.nextLabel || '',
    specialOffer: source?.dataset.specialOfferLabel || '',
    cartCrossSellHeadline: source?.dataset.cartCrossSellHeadline || '',
    cartCrossSellSubheadline: source?.dataset.cartCrossSellSubheadline || '',
    storefrontAccessToken: source?.dataset.storefrontAccessToken || '',
    storefrontApiVersion: source?.dataset.storefrontApiVersion || '',
    storefrontLanguageCode: source?.dataset.storefrontLanguageCode || 'EN',
  };
}

function getCurrencySymbol(currencyCode) {
  try {
    const locale = document.documentElement.lang || navigator.language;
    const currencyPart = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'symbol',
    })
      .formatToParts(0)
      .find((part) => part.type === 'currency');

    return currencyPart?.value || currencyCode;
  } catch (_error) {
    return currencyCode;
  }
}

/**
 * Replace currency codes in CandyRack-rendered price text without rebuilding
 * its markup or changing the underlying price value.
 *
 * @param {Element | null} element
 */
function localizeCurrencyCodes(element) {
  if (!(element instanceof Element)) return;

  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();

  while (textNode) {
    const currentValue = textNode.nodeValue || '';
    const localizedValue = currentValue.replace(/\b[A-Z]{3}\b/g, (currencyCode) =>
      getCurrencySymbol(currencyCode),
    );

    if (localizedValue !== currentValue) textNode.nodeValue = localizedValue;
    textNode = walker.nextNode();
  }
}

/**
 * @param {Element} card - Candy Rack offer card.
 * @returns {string}
 */
function getProductId(card) {
  const productIdClass = [...card.classList].find((className) => className.startsWith(PRODUCT_ID_CLASS_PREFIX));
  return productIdClass?.slice(PRODUCT_ID_CLASS_PREFIX.length) || '';
}

/**
 * @param {unknown} node - Rich text JSON node.
 * @returns {string[]}
 */
function getRichTextValues(node) {
  if (!node || typeof node !== 'object') return [];

  const values = [];

  if ('value' in node && typeof node.value === 'string') {
    values.push(node.value);
  }

  if ('children' in node && Array.isArray(node.children)) {
    for (const child of node.children) {
      values.push(...getRichTextValues(child));
    }
  }

  return values;
}

/**
 * @param {{ type?: string, value?: string } | null} metafield - Storefront API metafield.
 * @returns {string}
 */
function getMetafieldText(metafield) {
  if (!metafield?.value) return '';
  if (metafield.type !== 'rich_text_field') return metafield.value.trim();

  try {
    return getRichTextValues(JSON.parse(metafield.value)).join(' ').trim();
  } catch {
    return '';
  }
}

/**
 * @param {string[]} productIds - Shopify numeric product IDs.
 * @param {{ storefrontAccessToken: string, storefrontApiVersion: string, storefrontLanguageCode: string }} config - Storefront API configuration.
 * @returns {Promise<void>}
 */
async function fetchProductData(productIds, config) {
  for (const productId of productIds) {
    pendingProductIds.add(productId);
  }

  try {
    const response = await fetch(`/api/${config.storefrontApiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.storefrontAccessToken,
      },
      body: JSON.stringify({
        query: `
          query CrossSellProductData($ids: [ID!]!, $language: LanguageCode!) @inContext(language: $language) {
            nodes(ids: $ids) {
              ... on Product {
                id
                handle
                cardText: metafield(namespace: "custom", key: "card_text") {
                  type
                  value
                }
              }
            }
          }
        `,
        variables: {
          ids: productIds.map((productId) => `gid://shopify/Product/${productId}`),
          language: config.storefrontLanguageCode,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Storefront API request failed with status ${response.status}`);
    }

    const result = await response.json();

    if (result.errors) {
      throw new Error(result.errors.map((error) => error.message).join(', '));
    }

    for (const productId of productIds) {
      productDataCache.set(productId, { cardText: '', handle: '' });
    }

    for (const product of result.data?.nodes || []) {
      if (!product?.id) continue;

      const productId = product.id.split('/').pop();
      productDataCache.set(productId, {
        cardText: getMetafieldText(product.cardText),
        handle: product.handle || '',
      });
    }
  } catch (error) {
    console.error('Unable to load Candy Rack product data.', error);
  } finally {
    for (const productId of productIds) {
      pendingProductIds.delete(productId);
    }
  }
}

/**
 * @param {Element} card - Candy Rack slider-cart offer card.
 * @returns {string}
 */
function getCartOfferHandle(card) {
  const link = card.querySelector(
    '.candyrack-slider-cart-offer-item-header, .candyrack-slider-cart-offer-item-image-container a',
  );
  if (!(link instanceof HTMLAnchorElement)) return '';

  const productPath = new URL(link.href, window.location.origin).pathname.match(/\/products\/([^/]+)/);
  return productPath ? decodeURIComponent(productPath[1]) : '';
}

/**
 * @returns {Set<string>}
 */
function getSliderCartProductHandles() {
  const handles = new Set();

  for (const link of document.querySelectorAll(
    '#candyrack-slider-cart-wrapper .candyrack-slider-cart-item-image-container a[href*="/products/"]',
  )) {
    if (!(link instanceof HTMLAnchorElement)) continue;

    const productPath = new URL(link.href, window.location.origin).pathname.match(/\/products\/([^/]+)/);
    if (productPath) handles.add(decodeURIComponent(productPath[1]));
  }

  return handles;
}

/**
 * @param {string[]} handles - Shopify product handles.
 * @param {{ storefrontAccessToken: string, storefrontApiVersion: string, storefrontLanguageCode: string }} config - Storefront API configuration.
 * @returns {Promise<void>}
 */
async function fetchProductDataByHandles(handles, config) {
  for (const handle of handles) {
    pendingProductHandles.add(handle);
  }

  const variableDefinitions = ['$language: LanguageCode!', ...handles.map((_, index) => `$handle${index}: String!`)].join(
    ', ',
  );
  const productFields = handles
    .map(
      (_, index) => `
        product${index}: product(handle: $handle${index}) {
          handle
          cardText: metafield(namespace: "custom", key: "card_text") {
            type
            value
          }
        }
      `,
    )
    .join('');
  const variables = {
    language: config.storefrontLanguageCode,
    ...Object.fromEntries(handles.map((handle, index) => [`handle${index}`, handle])),
  };

  try {
    const response = await fetch(`/api/${config.storefrontApiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.storefrontAccessToken,
      },
      body: JSON.stringify({
        query: `
          query CrossSellProductDataByHandle(${variableDefinitions}) @inContext(language: $language) {
            ${productFields}
          }
        `,
        variables,
      }),
    });

    if (!response.ok) {
      throw new Error(`Storefront API request failed with status ${response.status}`);
    }

    const result = await response.json();

    if (result.errors) {
      throw new Error(result.errors.map((error) => error.message).join(', '));
    }

    for (const handle of handles) {
      productDataByHandleCache.set(handle, { cardText: '', handle: '' });
    }

    for (const [key, product] of Object.entries(result.data || {})) {
      if (!product) continue;

      const originalHandle = handles[Number(key.replace('product', ''))];
      const productData = {
        cardText: getMetafieldText(product.cardText),
        handle: product.handle || originalHandle,
      };
      productDataByHandleCache.set(originalHandle, productData);
      productDataByHandleCache.set(productData.handle, productData);
    }
  } catch (error) {
    console.error('Unable to load Candy Rack slider-cart product data.', error);
  } finally {
    for (const handle of handles) {
      pendingProductHandles.delete(handle);
    }
  }
}

/**
 * @param {string} handle - Current Shopify product handle.
 * @returns {string}
 */
function getProductUrl(handle) {
  const root = window.Shopify?.routes?.root || '/';
  const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
  return `${normalizedRoot}products/${encodeURIComponent(handle)}`;
}

/**
 * @param {Element} card - Candy Rack offer card.
 * @param {string} productUrl - Current Shopify product URL.
 * @returns {void}
 */
function updateProductNavigation(card, productUrl) {
  const title = card.querySelector('.candyrack-ppp-button-layout__info--product-name');

  if (title instanceof HTMLAnchorElement) {
    title.href = productUrl;
  }

  card.dataset.bospProductUrl = productUrl;
  if (card.dataset.bospProductNavigationBound === 'true') return;

  card.dataset.bospProductNavigationBound = 'true';
  card.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      const clickableProduct = event.target.closest(
        '.candyrack-ppp-button-layout__info--product-name, .candyrack-ppp-button-layout__image',
      );
      if (!clickableProduct || !card.contains(clickableProduct)) return;

      const currentProductUrl = card.dataset.bospProductUrl;
      if (!currentProductUrl) return;

      if (
        event instanceof MouseEvent &&
        (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      ) {
        return;
      }

      const quickViewEvent = new CustomEvent('bosp:candyrack-quick-view', {
        bubbles: true,
        cancelable: true,
        detail: { productUrl: currentProductUrl },
      });

      if (!card.dispatchEvent(quickViewEvent)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true,
  );
}

/**
 * @param {Element[]} cards - Candy Rack offer cards.
 * @param {{ storefrontAccessToken: string, storefrontApiVersion: string, storefrontLanguageCode: string }} config - Storefront API configuration.
 * @returns {Promise<void>}
 */
async function enhanceProductData(cards, config) {
  if (!config.storefrontAccessToken || !config.storefrontApiVersion) return;

  const productIds = cards.map(getProductId).filter(Boolean);
  const missingProductIds = [
    ...new Set(
      productIds.filter((productId) => !productDataCache.has(productId) && !pendingProductIds.has(productId)),
    ),
  ];

  if (missingProductIds.length) {
    await fetchProductData(missingProductIds, config);
  }

  for (const card of cards) {
    if (!card.isConnected) continue;

    const productId = getProductId(card);
    const productData = productDataCache.get(productId);
    if (!productData) continue;

    if (productData.handle) {
      updateProductNavigation(card, getProductUrl(productData.handle));
    }

    const title = card.querySelector('.candyrack-ppp-button-layout__info--product-name');
    const titleWrapper = title?.parentElement;
    if (!(titleWrapper instanceof HTMLElement)) continue;

    titleWrapper.classList.add('bosp-cross-sell__product-title-wrapper');
    if (!productData.cardText || card.querySelector('.bosp-cross-sell__description')) continue;

    const descriptionElement = document.createElement('p');
    descriptionElement.className = 'bosp-cross-sell__description';
    descriptionElement.textContent = productData.cardText;
    titleWrapper.insertAdjacentElement('afterend', descriptionElement);
  }
}

/**
 * @param {Element[]} cards - Candy Rack slider-cart offer cards.
 * @param {{ storefrontAccessToken: string, storefrontApiVersion: string, storefrontLanguageCode: string }} config - Storefront API configuration.
 * @returns {Promise<void>}
 */
async function enhanceCartOfferProductData(cards, config) {
  if (!config.storefrontAccessToken || !config.storefrontApiVersion) return;

  const handles = cards.map(getCartOfferHandle).filter(Boolean);
  const missingHandles = [
    ...new Set(
      handles.filter((handle) => !productDataByHandleCache.has(handle) && !pendingProductHandles.has(handle)),
    ),
  ];

  if (missingHandles.length) {
    await fetchProductDataByHandles(missingHandles, config);
  }

  for (const card of cards) {
    if (!card.isConnected) continue;

    const handle = getCartOfferHandle(card);
    const productData = productDataByHandleCache.get(handle);
    if (!productData) continue;

    if (productData.handle) {
      const productUrl = getProductUrl(productData.handle);

      for (const link of card.querySelectorAll(
        '.candyrack-slider-cart-offer-item-header, .candyrack-slider-cart-offer-item-image-container a',
      )) {
        if (link instanceof HTMLAnchorElement) link.href = productUrl;
      }
    }

    if (!productData.cardText || card.querySelector('.bosp-cross-sell__description')) continue;

    const titleWrapper = card.querySelector('.candyrack-slider-cart-offer-item-header-wrapper');
    if (!(titleWrapper instanceof HTMLElement)) continue;

    const descriptionElement = document.createElement('p');
    descriptionElement.className = 'bosp-cross-sell__description';
    descriptionElement.textContent = productData.cardText;
    titleWrapper.insertAdjacentElement('afterend', descriptionElement);
  }
}

function arrowIcon(direction) {
  const path = direction === 'previous' ? 'M6.5 11L1.5 6L6.5 1' : 'M1.5 1L6.5 6L1.5 11';

  return `
    <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <path d="${path}" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;
}

function cartIcon() {
  return `
    <svg class="bosp-cross-sell__cart-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path d="M4 5h3l2.4 11.3a2 2 0 0 0 2 1.7h7.2a2 2 0 0 0 2-1.6L22 8H6" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
      <circle cx="10" cy="21" r="1.4" stroke="currentColor" stroke-width="var(--icon-stroke-width)"/>
      <circle cx="18" cy="21" r="1.4" stroke="currentColor" stroke-width="var(--icon-stroke-width)"/>
    </svg>
  `;
}

function checkIcon() {
  return `
    <svg class="bosp-cross-sell__check-icon" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path d="M7.25165 12.6908C7.25153 12.6906 7.25177 12.6909 7.25165 12.6908C7.54455 12.9837 8.01951 12.9838 8.31241 12.6909C8.31581 12.6875 8.31917 12.684 8.32248 12.6806L14.3227 6.68035C14.6156 6.38746 14.6156 5.91259 14.3227 5.61969C14.0298 5.3268 13.555 5.3268 13.2621 5.61969L7.78198 11.0998L5.13043 8.44822C4.83753 8.15532 4.36266 8.15532 4.06977 8.44822C3.77687 8.74111 3.77687 9.21598 4.06977 9.50888L7.25165 12.6908Z" fill="white"/>
    </svg>
  `;
}

/**
 * @param {Element} element - Element receiving the attribute.
 * @param {string} name - Attribute name.
 * @param {string} value - Attribute value.
 * @returns {void}
 */
function setAttributeIfChanged(element, name, value) {
  if (element.getAttribute(name) === value) return;
  element.setAttribute(name, value);
}

/**
 * @param {HTMLElement} parent - Parent containing the target.
 * @param {Element} target - Descendant whose direct wrapper is needed.
 * @returns {HTMLElement | null}
 */
function getDirectChild(parent, target) {
  let child = target;

  while (child.parentElement && child.parentElement !== parent) {
    child = child.parentElement;
  }

  return child instanceof HTMLElement && child.parentElement === parent ? child : null;
}

function createCarouselNavigation(rail, labels) {
  const navigation = document.createElement('div');
  navigation.className = 'bosp-cross-sell__nav';

  for (const direction of ['previous', 'next']) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bosp-cross-sell__arrow button button-unstyled button-unstyled--transparent';
    button.dataset.crossSellDirection = direction;
    button.setAttribute('aria-label', direction === 'previous' ? labels.previous : labels.next);
    button.innerHTML = arrowIcon(direction);
    button.addEventListener('click', () => {
      const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const isAtStart = rail.scrollLeft <= 1;
      const isAtEnd = rail.scrollLeft >= maxScroll - 1;

      if (direction === 'previous' && isAtStart) {
        rail.scrollTo({ left: maxScroll, behavior: 'smooth' });
        return;
      }

      if (direction === 'next' && isAtEnd) {
        rail.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }

      rail.scrollBy({
        left: (direction === 'previous' ? -1 : 1) * Math.max(rail.clientWidth * 0.75, 180),
        behavior: 'smooth',
      });
    });
    navigation.append(button);
  }

  const update = () => {
    const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth - 1);
    navigation.hidden = maxScroll <= 1;
  };

  rail.addEventListener('scroll', update, { passive: true });
  new ResizeObserver(update).observe(rail);
  requestAnimationFrame(update);

  return navigation;
}

function enhanceCandyRackBlock(root) {
  const rail = root.querySelector('.candyrack-ppp-button-layout__offers--list');
  const header = root.querySelector('.candyrack-ppp-button-layout__offers');
  if (!(rail instanceof HTMLElement) || !(header instanceof HTMLElement)) return;

  const config = getConfig();
  const title = header.querySelector('.candyrack-ppp-button-layout__offers--title');
  const subtitle = header.querySelector('[class*="subtitle" i]');

  if (title instanceof HTMLElement && subtitle instanceof HTMLElement) {
    const titleItem = getDirectChild(header, title);
    const subtitleItem = getDirectChild(header, subtitle);

    subtitle.classList.add('bosp-cross-sell__subtitle-text');

    if (titleItem && titleItem === subtitleItem) {
      titleItem.classList.add('bosp-cross-sell__heading-group');
    } else {
      titleItem?.classList.add('bosp-cross-sell__title-item');
      subtitleItem?.classList.add('bosp-cross-sell__subtitle-item');
    }
  }

  if (!header.querySelector('.bosp-cross-sell__nav')) {
    header.append(createCarouselNavigation(rail, config));
  }

  const cards = [...rail.querySelectorAll('.candyrack-ppp-button-layout__offer')];

  for (const [index, card] of cards.entries()) {
    if (index === 0 && config.specialOffer && !card.querySelector('.bosp-cross-sell__app-pill')) {
      const pill = document.createElement('span');
      pill.className = 'bosp-cross-sell__app-pill';
      pill.textContent = config.specialOffer;
      card.prepend(pill);
    }

    const addButton = card.querySelector('.candyrack-ppp-button');
    if (addButton instanceof HTMLButtonElement && !addButton.querySelector('.bosp-cross-sell__cart-icon')) {
      addButton.insertAdjacentHTML('afterbegin', cartIcon());
    }

    const regularPrice = card.querySelector('.candyrack-ppp-button-layout__info--product-price');
    const discountPrice = card.querySelector('.candyrack-ppp-button-layout__info--product-discount-price');
    localizeCurrencyCodes(regularPrice);
    localizeCurrencyCodes(discountPrice);

    if (regularPrice && discountPrice && regularPrice.parentElement === discountPrice.parentElement) {
      regularPrice.parentElement.classList.add('bosp-cross-sell__price-row');
    }
  }

  void enhanceProductData(cards, config);
}

/**
 * @param {HTMLElement} header - Candy Rack offers header.
 * @param {HTMLElement} title - Candy Rack offers title.
 * @param {{ cartCrossSellHeadline: string, cartCrossSellSubheadline: string }} config - Theme copy.
 * @returns {void}
 */
function applyCartCrossSellHeading(header, title, config) {
  const signature = `${config.cartCrossSellSubheadline}\n${config.cartCrossSellHeadline}`;
  if (header.dataset.bospCrossSellHeading === signature) return;

  let subheadline = header.querySelector('.bosp-cross-sell__cart-subheadline');
  if (!(subheadline instanceof HTMLElement)) {
    subheadline = document.createElement('p');
    subheadline.className = 'bosp-cross-sell__cart-subheadline';
    header.prepend(subheadline);
  }

  subheadline.textContent = config.cartCrossSellSubheadline;
  subheadline.hidden = config.cartCrossSellSubheadline === '';

  if (config.cartCrossSellHeadline) {
    const discountMatch = config.cartCrossSellHeadline.match(/^(.*?)([−-]\s*\d+\s*%)\s*$/);

    if (discountMatch) {
      const highlight = document.createElement('span');
      highlight.className = 'bosp-cross-sell__cart-headline-highlight';
      highlight.textContent = discountMatch[2];
      title.replaceChildren(document.createTextNode(`${discountMatch[1].trimEnd()} `), highlight);
    } else {
      title.textContent = config.cartCrossSellHeadline;
    }
  }

  header.dataset.bospCrossSellHeading = signature;
}

function enhanceCandyRackCartOffers(root) {
  const rail = root.querySelector('.candyrack-slider-cart-offers-list');
  const header = root.querySelector('.candyrack-slider-cart-offers-header-wrapper');
  const title = root.querySelector('.candyrack-slider-cart-offers-title');
  if (!(rail instanceof HTMLElement) || !(header instanceof HTMLElement) || !(title instanceof HTMLElement)) return;

  const config = getConfig();
  const isCartPageMirror = Boolean(root.closest(CART_PAGE_MOUNT_SELECTOR));
  setAttributeIfChanged(title, 'id', isCartPageMirror ? 'bosp-cart-page-cross-sell-title' : 'bosp-cart-cross-sell-title');
  applyCartCrossSellHeading(header, title, config);
  setAttributeIfChanged(root, 'role', 'region');
  setAttributeIfChanged(root, 'aria-roledescription', 'carousel');
  setAttributeIfChanged(root, 'aria-labelledby', title.id);
  setAttributeIfChanged(rail, 'aria-live', 'polite');

  if (!header.querySelector('.bosp-cross-sell__nav')) {
    header.append(createCarouselNavigation(rail, config));
  }

  const renderedCards = [...rail.querySelectorAll('.candyrack-slider-cart-offer-item-wrapper')];
  const cards = [];
  const seenProducts = new Set();

  for (const card of renderedCards) {
    const productHandle = getCartOfferHandle(card);
    const productTitle = card.querySelector('.candyrack-slider-cart-offer-item-header')?.textContent?.trim().toLowerCase();
    const productKey = productHandle || productTitle || '';
    const isDuplicate = productKey !== '' && seenProducts.has(productKey);

    card.classList.toggle('bosp-cross-sell__offer--duplicate', isDuplicate);

    if (isDuplicate) {
      setAttributeIfChanged(card, 'aria-hidden', 'true');
      continue;
    }

    if (productKey) seenProducts.add(productKey);
    card.removeAttribute('aria-hidden');
    cards.push(card);
  }

  const cartProductHandles = getSliderCartProductHandles();

  for (const [index, card] of cards.entries()) {
    const productTitle = card.querySelector('.candyrack-slider-cart-offer-item-header')?.textContent?.trim();
    const productHandle = getCartOfferHandle(card);
    const isAdded = cartProductHandles.has(productHandle);

    if (isAdded) pendingCartOfferHandles.delete(productHandle);

    card.classList.toggle('bosp-cross-sell__offer--added', isAdded);
    card.classList.toggle('bosp-cross-sell__offer--pending', !isAdded && pendingCartOfferHandles.has(productHandle));
    setAttributeIfChanged(card, 'role', 'group');
    setAttributeIfChanged(card, 'aria-roledescription', 'slide');
    setAttributeIfChanged(card, 'aria-label', `${productTitle || index + 1} (${index + 1}/${cards.length})`);

    if (!card.querySelector('.bosp-cross-sell__check-icon')) {
      card.insertAdjacentHTML('beforeend', checkIcon());
    }

    if (index === 0 && config.specialOffer && !card.querySelector('.bosp-cross-sell__app-pill')) {
      const pill = document.createElement('span');
      pill.className = 'bosp-cross-sell__app-pill';
      pill.textContent = config.specialOffer;
      card.prepend(pill);
    }

    const addButton = card.querySelector('.candyrack-slider-cart-offer-item-add-button');
    if (addButton instanceof HTMLButtonElement && !addButton.querySelector('.bosp-cross-sell__cart-icon')) {
      addButton.insertAdjacentHTML('afterbegin', cartIcon());
    }

    if (addButton instanceof HTMLButtonElement && addButton.dataset.bospAddedStateBound !== 'true') {
      addButton.dataset.bospAddedStateBound = 'true';
      addButton.addEventListener('click', () => {
        const currentHandle = getCartOfferHandle(card);
        if (!currentHandle) return;

        pendingCartOfferHandles.add(currentHandle);
        card.classList.add('bosp-cross-sell__offer--pending');

        window.setTimeout(() => {
          pendingCartOfferHandles.delete(currentHandle);
          scheduleCandyRackScan();
        }, 6000);
      });
    }

    const priceRow = card.querySelector('.candyrack-slider-cart-offer-price-wrapper');
    localizeCurrencyCodes(priceRow);
    priceRow?.classList.add('bosp-cross-sell__price-row');
  }

  void enhanceCartOfferProductData(cards, config);
}

/**
 * @param {Element} mirrorCard - Card rendered in the cart-page mirror.
 * @returns {Element | null}
 */
function getSourceCartOfferCard(mirrorCard) {
  const sourceRoot = document.querySelector(
    '#candyrack-slider-cart-wrapper .candyrack-slider-cart-offers-wrapper',
  );
  if (!(sourceRoot instanceof HTMLElement)) return null;

  const handle = getCartOfferHandle(mirrorCard);
  const title = mirrorCard.querySelector('.candyrack-slider-cart-offer-item-header')?.textContent?.trim();

  for (const sourceCard of sourceRoot.querySelectorAll('.candyrack-slider-cart-offer-item-wrapper')) {
    if (handle && getCartOfferHandle(sourceCard) === handle) return sourceCard;

    const sourceTitle = sourceCard.querySelector('.candyrack-slider-cart-offer-item-header')?.textContent?.trim();
    if (!handle && title && sourceTitle === title) return sourceCard;
  }

  return null;
}

/**
 * @param {HTMLElement} mount - Cart-page cross-sell mount.
 * @returns {void}
 */
function bindCartPageOfferProxy(mount) {
  if (mount.dataset.bospOfferProxyBound === 'true') return;
  mount.dataset.bospOfferProxyBound = 'true';

  mount.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const mirrorButton = event.target.closest('.candyrack-slider-cart-offer-item-add-button');
    const mirrorCard = mirrorButton?.closest('.candyrack-slider-cart-offer-item-wrapper');
    if (!(mirrorButton instanceof HTMLButtonElement) || !(mirrorCard instanceof HTMLElement)) return;

    const sourceCard = getSourceCartOfferCard(mirrorCard);
    const sourceButton = sourceCard?.querySelector('.candyrack-slider-cart-offer-item-add-button');
    if (!(sourceButton instanceof HTMLButtonElement)) return;

    event.preventDefault();
    sourceButton.click();
  });

  mount.addEventListener('change', (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const mirrorCard = event.target.closest('.candyrack-slider-cart-offer-item-wrapper');
    if (!(mirrorCard instanceof HTMLElement)) return;

    const sourceCard = getSourceCartOfferCard(mirrorCard);
    if (!(sourceCard instanceof HTMLElement)) return;

    const mirrorSelects = [...mirrorCard.querySelectorAll('select')];
    const sourceSelects = [...sourceCard.querySelectorAll('select')];
    const selectIndex = mirrorSelects.indexOf(event.target);
    const sourceSelect = sourceSelects[selectIndex];
    if (!(sourceSelect instanceof HTMLSelectElement)) return;

    sourceSelect.value = event.target.value;
    sourceSelect.dispatchEvent(new Event('input', { bubbles: true }));
    sourceSelect.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

function syncCartPageOffers() {
  const mount = document.querySelector(CART_PAGE_MOUNT_SELECTOR);
  if (!(mount instanceof HTMLElement)) return;

  const source = document.querySelector(
    '#candyrack-slider-cart-wrapper .candyrack-slider-cart-offers-wrapper',
  );
  if (!(source instanceof HTMLElement)) {
    mount.hidden = true;
    return;
  }

  const sourceMarkup = source.innerHTML;
  if (cartPageSourceMarkup === sourceMarkup && mount.firstElementChild) return;

  const mirror = source.cloneNode(true);
  if (!(mirror instanceof HTMLElement)) return;

  mirror.classList.add('bosp-cart-page-cross-sell__offers');
  mirror.querySelector('.bosp-cross-sell__nav')?.remove();

  for (const element of mirror.querySelectorAll('[id]')) {
    element.removeAttribute('id');
  }

  for (const button of mirror.querySelectorAll('[data-bosp-added-state-bound]')) {
    button.removeAttribute('data-bosp-added-state-bound');
  }

  cartPageSourceMarkup = sourceMarkup;
  mount.replaceChildren(mirror);
  mount.hidden = false;
  bindCartPageOfferProxy(mount);
  enhanceCandyRackCartOffers(mirror);
}

/**
 * Candy Rack injects its filled- and empty-cart titles as h2 elements.
 * Preserve the app's hooks and appearance while keeping them out of the
 * page's content heading outline.
 */
function demoteCandyRackCartHeadings() {
  const typographyProperties = [
    'color',
    'font-family',
    'font-feature-settings',
    'font-kerning',
    'font-optical-sizing',
    'font-size',
    'font-stretch',
    'font-style',
    'font-variant',
    'font-variation-settings',
    'font-weight',
    'letter-spacing',
    'line-height',
    'text-transform',
  ];

  const titles = document.querySelectorAll(
    '#candyrack-slider-cart-title, .candyrack-slider-cart-empty-cart-title',
  );

  for (const title of titles) {
    if (!(title instanceof HTMLHeadingElement)) continue;

    for (const element of [title, ...title.querySelectorAll('*')]) {
      const computedStyle = window.getComputedStyle(element);

      for (const property of typographyProperties) {
        element.style.setProperty(property, computedStyle.getPropertyValue(property));
      }
    }

    const replacement = document.createElement('div');

    for (const attribute of title.attributes) {
      replacement.setAttribute(attribute.name, attribute.value);
    }

    replacement.append(...title.childNodes);
    title.replaceWith(replacement);
  }
}

function scanCandyRackBlocks() {
  demoteCandyRackCartHeadings();

  for (const root of document.querySelectorAll('.candyrack-ppp-button-layout')) {
    enhanceCandyRackBlock(root);
  }

  for (const root of document.querySelectorAll('.candyrack-slider-cart-offers-wrapper')) {
    enhanceCandyRackCartOffers(root);
  }

  syncCartPageOffers();
}

function scheduleCandyRackScan() {
  if (candyRackScanScheduled) return;

  candyRackScanScheduled = true;
  requestAnimationFrame(() => {
    candyRackScanScheduled = false;
    scanCandyRackBlocks();
  });
}

scanCandyRackBlocks();

const candyRackObserver = new MutationObserver(scheduleCandyRackScan);
candyRackObserver.observe(document.documentElement, { childList: true, subtree: true });
